/**
 * Vendor Marketplace Service
 *
 * Handles all vendor marketplace operations including opportunities, bids, and portfolio management
 */

import {
  ServiceOpportunity,
  VendorGroupBuyRequest,
  VendorService,
  VendorContract,
  VendorBid,
  GroupBuyResponse,
  CreateVendorServiceRequest,
  BidSubmissionResult,
  ResponseResult,
  UpdateResult,
} from '@/types/entities/vendor/bridge/marketplace';

class VendorMarketplaceService {
  private baseUrl = '/api/vendor/marketplace';
  // Track simulator state/intervals so we can avoid duplicates and clean up on HMR
  private _realtimeStarted = false;
  private _intervals: number[] = [];

  // Service Opportunities
  async getServiceOpportunities(
    vendorId: string,
    filters?: {
      category?: string[];
      type?: string[];
      priority?: string[];
      location?: string[];
    },
  ): Promise<ServiceOpportunity[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', vendorId);

      if (filters) {
        Object.entries(filters).forEach(([key, values]) => {
          if (values && values.length > 0) {
            values.forEach((value) => queryParams.append(key, value));
          }
        });
      }

      const response = await fetch(`${this.baseUrl}/opportunities?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch opportunities: ${response.statusText}`);
      }

      const data = await response.json();
      return data.opportunities || [];
    } catch (error) {
      console.error('Error fetching service opportunities:', error);
      throw error;
    }
  }

  async getOpportunityDetails(opportunityId: string): Promise<ServiceOpportunity | null> {
    try {
      const response = await fetch(`${this.baseUrl}/opportunities/${opportunityId}`);

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch opportunity details: ${response.statusText}`);
      }

      const data = await response.json();
      return data.opportunity;
    } catch (error) {
      console.error('Error fetching opportunity details:', error);
      throw error;
    }
  }

  async submitBid(
    bid: Omit<VendorBid, 'id' | 'submittedAt' | 'status'>,
  ): Promise<BidSubmissionResult> {
    try {
      const response = await fetch(`${this.baseUrl}/bids`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...bid,
          submittedAt: new Date().toISOString(),
          status: 'pending',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit bid',
          error: data.error,
        };
      }

      return {
        success: true,
        bidId: data.bidId,
        message: 'Bid submitted successfully',
      };
    } catch (error) {
      console.error('Error submitting bid:', error);
      return {
        success: false,
        message: 'Failed to submit bid',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async getVendorBids(vendorId: string, opportunityId?: string): Promise<VendorBid[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', vendorId);
      if (opportunityId) {
        queryParams.append('opportunityId', opportunityId);
      }

      const response = await fetch(`${this.baseUrl}/bids?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch bids: ${response.statusText}`);
      }

      const data = await response.json();
      return data.bids || [];
    } catch (error) {
      console.error('Error fetching vendor bids:', error);
      throw error;
    }
  }

  async updateBid(bidId: string, updates: Partial<VendorBid>): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/bids/${bidId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to update bid',
          error: data.error,
        };
      }

      return {
        success: true,
        data: data.bid,
        message: 'Bid updated successfully',
      };
    } catch (error) {
      console.error('Error updating bid:', error);
      return {
        success: false,
        message: 'Failed to update bid',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async withdrawBid(bidId: string): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/bids/${bidId}/withdraw`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to withdraw bid',
          error: data.error,
        };
      }

      return {
        success: true,
        message: 'Bid withdrawn successfully',
      };
    } catch (error) {
      console.error('Error withdrawing bid:', error);
      return {
        success: false,
        message: 'Failed to withdraw bid',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Group Buy Requests
  async getGroupBuyRequests(
    vendorId: string,
    filters?: {
      status?: string[];
      type?: string[];
      category?: string[];
    },
  ): Promise<VendorGroupBuyRequest[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', vendorId);

      if (filters) {
        Object.entries(filters).forEach(([key, values]) => {
          if (values && values.length > 0) {
            values.forEach((value) => queryParams.append(key, value));
          }
        });
      }

      const response = await fetch(`${this.baseUrl}/group-buy-requests?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch group buy requests: ${response.statusText}`);
      }

      const data = await response.json();
      return data.requests || [];
    } catch (error) {
      console.error('Error fetching group buy requests:', error);
      throw error;
    }
  }

  async getGroupBuyRequestDetails(requestId: string): Promise<VendorGroupBuyRequest | null> {
    try {
      const response = await fetch(`${this.baseUrl}/group-buy-requests/${requestId}`);

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch group buy request details: ${response.statusText}`);
      }

      const data = await response.json();
      return data.request;
    } catch (error) {
      console.error('Error fetching group buy request details:', error);
      throw error;
    }
  }

  async respondToGroupBuyRequest(
    requestId: string,
    vendorId: string,
    response: GroupBuyResponse,
  ): Promise<ResponseResult> {
    try {
      const apiResponse = await fetch(`${this.baseUrl}/group-buy-requests/${requestId}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorId,
          response,
          submittedAt: new Date().toISOString(),
        }),
      });

      const data = await apiResponse.json();

      if (!apiResponse.ok) {
        return {
          success: false,
          message: data.message || 'Failed to submit response',
          error: data.error,
        };
      }

      return {
        success: true,
        responseId: data.responseId,
        message: 'Response submitted successfully',
      };
    } catch (error) {
      console.error('Error responding to group buy request:', error);
      return {
        success: false,
        message: 'Failed to submit response',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Service Portfolio
  async getVendorPortfolio(vendorId: string): Promise<VendorService[]> {
    try {
      const response = await fetch(`${this.baseUrl}/portfolio/${vendorId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch vendor portfolio: ${response.statusText}`);
      }

      const data = await response.json();
      return data.services || [];
    } catch (error) {
      console.error('Error fetching vendor portfolio:', error);
      throw error;
    }
  }

  async createService(
    vendorId: string,
    service: CreateVendorServiceRequest,
  ): Promise<VendorService> {
    try {
      const response = await fetch(`${this.baseUrl}/portfolio/${vendorId}/services`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...service,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create service');
      }

      const data = await response.json();
      return data.service;
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  }

  async updateService(serviceId: string, updates: Partial<VendorService>): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/services/${serviceId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updates,
          updatedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to update service',
          error: data.error,
        };
      }

      return {
        success: true,
        data: data.service,
        message: 'Service updated successfully',
      };
    } catch (error) {
      console.error('Error updating service:', error);
      return {
        success: false,
        message: 'Failed to update service',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async deleteService(serviceId: string): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/services/${serviceId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to delete service',
          error: data.error,
        };
      }

      return {
        success: true,
        message: 'Service deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting service:', error);
      return {
        success: false,
        message: 'Failed to delete service',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async toggleServiceStatus(serviceId: string, isActive: boolean): Promise<UpdateResult> {
    return this.updateService(serviceId, { isActive });
  }

  // Contracts
  async getVendorContracts(
    vendorId: string,
    filters?: {
      status?: string[];
      type?: string[];
      dateRange?: { from: string; to: string };
    },
  ): Promise<VendorContract[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', vendorId);

      if (filters) {
        if (filters.status && filters.status.length > 0) {
          filters.status.forEach((status) => queryParams.append('status', status));
        }
        if (filters.type && filters.type.length > 0) {
          filters.type.forEach((type) => queryParams.append('type', type));
        }
        if (filters.dateRange) {
          queryParams.append('dateFrom', filters.dateRange.from);
          queryParams.append('dateTo', filters.dateRange.to);
        }
      }

      const response = await fetch(`${this.baseUrl}/contracts?${queryParams}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch vendor contracts: ${response.statusText}`);
      }

      const data = await response.json();
      return data.contracts || [];
    } catch (error) {
      console.error('Error fetching vendor contracts:', error);
      throw error;
    }
  }

  async getContractDetails(contractId: string): Promise<VendorContract | null> {
    try {
      const response = await fetch(`${this.baseUrl}/contracts/${contractId}`);

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch contract details: ${response.statusText}`);
      }

      const data = await response.json();
      return data.contract;
    } catch (error) {
      console.error('Error fetching contract details:', error);
      throw error;
    }
  }

  async updateContractStatus(
    contractId: string,
    status: VendorContract['status'],
    notes?: string,
  ): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/contracts/${contractId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status,
          notes,
          updatedAt: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to update contract status',
          error: data.error,
        };
      }

      return {
        success: true,
        data: data.contract,
        message: 'Contract status updated successfully',
      };
    } catch (error) {
      console.error('Error updating contract status:', error);
      return {
        success: false,
        message: 'Failed to update contract status',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addContractMessage(
    contractId: string,
    message: {
      senderId: string;
      senderType: 'vendor' | 'resident' | 'admin';
      message: string;
      messageType: 'general' | 'change_request' | 'approval' | 'complaint' | 'update';
      attachments?: string[];
    },
  ): Promise<UpdateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/contracts/${contractId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...message,
          timestamp: new Date().toISOString(),
          read: false,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to add message',
          error: data.error,
        };
      }

      return {
        success: true,
        data: data.message,
        message: 'Message sent successfully',
      };
    } catch (error) {
      console.error('Error adding contract message:', error);
      return {
        success: false,
        message: 'Failed to send message',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Real-time updates (WebSocket-like functionality)
  private listeners: Map<string, Set<(data: unknown) => void>> = new Map();

  subscribe(event: string, callback: (data: unknown) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    };
  }

  private emit(event: string, data: unknown): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(data));
    }
  }

  // Mock real-time events for development
  public simulateRealTimeEvents(): void {
    if (this._realtimeStarted) return;
    this._realtimeStarted = true;

    // Simulate new opportunities
    const opp = window.setInterval(() => {
      this.emit('new_opportunity', {
        id: `opp-${Date.now()}`,
        type: 'service_request',
        title: 'New Service Request',
        priority: 'medium',
      });
    }, 30000); // Every 30 seconds
    this._intervals.push(opp);

    // Simulate bid status updates
    const bid = window.setInterval(() => {
      this.emit('bid_status_update', {
        bidId: `bid-${Date.now()}`,
        status: 'accepted',
        message: 'Your bid has been accepted!',
      });
    }, 60000); // Every minute
    this._intervals.push(bid);
  }

  public stopRealTimeEvents(): void {
    this._intervals.forEach((id) => clearInterval(id));
    this._intervals = [];
    this._realtimeStarted = false;
  }
}

// Create singleton instance
export const vendorMarketplaceService = new VendorMarketplaceService();

// Start simulating real-time events in development
if (import.meta.env.DEV) {
  vendorMarketplaceService.simulateRealTimeEvents();
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      vendorMarketplaceService.stopRealTimeEvents();
    });
  }
}
